/**
 * FO-307-A1 - pure logica voor de offline-mutatie-wachtrij van de portal.
 *
 * Framework- en storage-vrij (geen IndexedDB, geen React), zodat de kern in
 * `@streep/utils` getoetst wordt (de gate draait deze suite). De portal levert
 * de IndexedDB- en React-laag eromheen (`apps/portal/src/lib/offline/*`).
 *
 * Conflictregel (CLAUDE.md / FO-307 §8): de SERVER wint. Een 409 markeert de
 * mutatie als `conflict` (lokale kopie behouden, monteur ziet een melding); een
 * netwerkfout houdt 'm `pending` voor een latere poging; een 4xx (validatie/
 * autorisatie) markeert 'm `failed` (blijft zichtbaar, vervalt nooit stil).
 */

/** Status van een wachtrij-item. `synced` items worden verwijderd, niet bewaard. */
export type OfflineMutationStatus = 'pending' | 'syncing' | 'conflict' | 'failed'

/** De velden die de pure logica van een wachtrij-item nodig heeft. */
export interface OfflineMutationCore {
  id: string
  /** Client-gegenereerde sleutel; ongewijzigd bij elke retry (server-dedup). */
  idempotencyKey: string
  /** Epoch-ms van aanmaak - bepaalt de sync-volgorde (FIFO). */
  createdAt: number
  status: OfflineMutationStatus
  attempts: number
  lastError?: string
}

/** Uitkomst van een sync-poging, genormaliseerd los van de transportlaag. */
export type SyncOutcome =
  | { kind: 'network-error' }
  | { kind: 'http'; ok: boolean; status: number }

/** Beslissing die uit een uitkomst volgt. */
export type SyncDecision = 'success' | 'retry' | 'conflict' | 'failed'

/**
 * Genereert een idempotency-key. `crypto.randomUUID` is beschikbaar in Node 20+
 * en alle doel-browsers; valt terug op een timestamp-random combinatie voor
 * exotische runtimes (nooit `Math.random` als enige bron van uniciteit in de DB,
 * maar hier alleen een client-side sleutel).
 */
export function newIdempotencyKey(): string {
  // Structureel getypeerd (geen DOM `Crypto`-type) zodat dit ook in de Node-
  // context van de API compileert; `globalThis.crypto.randomUUID` bestaat in
  // Node 20+ en alle doel-browsers.
  const c = (globalThis as { crypto?: { randomUUID?: () => string } }).crypto
  if (c && typeof c.randomUUID === 'function') return c.randomUUID()
  // Fallback: tijd + twee random-blokken. Botsingskans verwaarloosbaar voor een
  // per-toestel wachtrij.
  const rnd = () => Math.floor(Math.random() * 0xffffffff).toString(16).padStart(8, '0')
  return `k-${Date.now().toString(16)}-${rnd()}-${rnd()}`
}

/**
 * Sorteert de wachtrij voor het afspelen: strikt FIFO op `createdAt` (tiebreak
 * `id`). Zo gaat "uren invullen" (eerder gedaan) vóór "handtekening zetten"
 * (later) de deur uit - de volgorde waarin de monteur ze deed blijft behouden,
 * wat serverside afhankelijkheden respecteert.
 */
export function sortMutationsForSync<T extends Pick<OfflineMutationCore, 'createdAt' | 'id'>>(
  items: readonly T[],
): T[] {
  return [...items].sort((a, b) => a.createdAt - b.createdAt || (a.id < b.id ? -1 : a.id > b.id ? 1 : 0))
}

/** De items die de gebruiker als "nog niet gesynchroniseerd" moet zien. */
export function isUnsynced(m: Pick<OfflineMutationCore, 'status'>): boolean {
  return m.status === 'pending' || m.status === 'syncing'
}

/** Aantal items dat nog op verbinding wacht (de "n wachtend"-badge). */
export function countPending(items: readonly Pick<OfflineMutationCore, 'status'>[]): number {
  return items.filter(isUnsynced).length
}

/** Aantal items dat aandacht vraagt (conflict of definitieve fout). */
export function countNeedsAttention(items: readonly Pick<OfflineMutationCore, 'status'>[]): number {
  return items.filter((m) => m.status === 'conflict' || m.status === 'failed').length
}

/**
 * Vertaalt een sync-uitkomst naar een beslissing. De server wint altijd:
 * - netwerkfout / 5xx  -> `retry` (blijft in de wachtrij)
 * - 2xx                -> `success` (verwijderen)
 * - 409                -> `conflict` (lokale kopie bewaren, melden)
 * - overige 4xx        -> `failed` (blijft zichtbaar met de fout)
 */
export function classifySyncOutcome(outcome: SyncOutcome): SyncDecision {
  if (outcome.kind === 'network-error') return 'retry'
  if (outcome.ok) return 'success'
  if (outcome.status === 409) return 'conflict'
  if (outcome.status >= 500) return 'retry'
  return 'failed'
}

/** Resultaat van `applyOutcome`: verwijderen of het item bijwerken. */
export type QueueUpdate<T extends OfflineMutationCore> =
  | { action: 'remove'; id: string; decision: 'success' }
  | { action: 'update'; mutation: T; decision: Exclude<SyncDecision, 'success'> }

/**
 * Past een uitkomst toe op een wachtrij-item en geeft de volgende wachtrij-actie
 * terug. Zuiver: geen side-effects, de caller voert de IndexedDB-mutatie uit.
 */
export function applyOutcome<T extends OfflineMutationCore>(
  mutation: T,
  outcome: SyncOutcome,
  errorMessage?: string,
): QueueUpdate<T> {
  const decision = classifySyncOutcome(outcome)
  if (decision === 'success') {
    return { action: 'remove', id: mutation.id, decision }
  }
  const status: OfflineMutationStatus =
    decision === 'conflict' ? 'conflict' : decision === 'failed' ? 'failed' : 'pending'
  return {
    action: 'update',
    decision,
    mutation: {
      ...mutation,
      status,
      attempts: mutation.attempts + 1,
      lastError: errorMessage ?? mutation.lastError,
    },
  }
}
